import React from 'react';
import styled from 'styled-components';

const StyledDashedSpinner = styled.div`
  .dashed-spinner {
    position: relative;
    display: inline-block;

    &__track {
      stroke: var(--gray-dark);
      stroke-linecap: round;
      fill: none;
    }

    &__progress {
      fill: none;
      stroke: var(--red-main);
      stroke-linecap: round;
      transform-origin: center center;
      animation: dashed-spinner-rotate 1.5s linear infinite;
    }
  }

  @keyframes dashed-spinner-rotate {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }
`;

type DashedSpinnerProps = {
  size?: number;
  strokeWidth?: number;
  duration?: number;
  className?: string;
};

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number,
): { x: number; y: number } => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describeArcPath = (
  x: number,
  y: number,
  radius: number,
  startAngle: number,
  endAngle: number,
): string => {
  const start = polarToCartesian(x, y, radius, startAngle);
  const end = polarToCartesian(x, y, radius, endAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

  return [
    'M',
    start.x,
    start.y,
    'A',
    radius,
    radius,
    0,
    largeArcFlag,
    1,
    end.x,
    end.y,
  ].join(' ');
};

const SEGMENT_LENGTH = 4;
const GAP_LENGTH = 3;
const ARC_START_ANGLE = -120;
const ARC_END_ANGLE = 30;

export const DashedSpinner: React.FC<DashedSpinnerProps> = ({
  size = 50,
  strokeWidth = 5,
  duration = 1.5,
  className,
}) => {
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;

  const arcPath = describeArcPath(
    center,
    center,
    radius,
    ARC_START_ANGLE,
    ARC_END_ANGLE,
  );

  return (
    <StyledDashedSpinner>
      <svg
        className={`dashed-spinner ${className ?? ''}`}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
      >
        <circle
          className="dashed-spinner__track"
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <path
          className="dashed-spinner__progress"
          d={arcPath}
          strokeWidth={strokeWidth}
          strokeDasharray={`${SEGMENT_LENGTH} ${GAP_LENGTH}`}
          style={{ animationDuration: `${duration}s` }}
        />
      </svg>
    </StyledDashedSpinner>
  );
};
